import { FC } from "react";
import { Playable, CardKinds, GameResource } from "../types";
import { GetPlayable } from "../constants";
import { getValueColor, getResourceSprite } from "../utils/tileOverlayUtils";
import Sprite from "./Sprite";
import { formatNumber } from "../utils/formatting";
import { isMobile } from "../utils/mobile";

function cardSprite({
  kind,
  level,
}: {
  kind: Playable["kind"];
  level: Playable["level"];
}) {
  const spriteIdx = CardKinds.indexOf(kind);
  return { y: spriteIdx, x: level };
}

export interface PlayableTooltipProps {
  playable: Playable;
  visible: boolean;
  mousePosition: { x: number; y: number };
  source?: "hand" | "building";
}

export const PlayableTooltip: FC<PlayableTooltipProps> = ({
  playable,
  visible,
  mousePosition,
  source = "building",
}) => {
  if (!visible) return null;

  // Separate positive and negative resources
  const provides = Object.entries(playable.resources).filter(
    ([, value]) => value > 0,
  );
  const costs = Object.entries(playable.resources).filter(
    ([, value]) => value < 0,
  );

  // Get combination target if it exists
  const combinesInto = playable.combines.into
    ? GetPlayable(playable.combines.into)
    : null;

  // Calculate tooltip position to avoid screen edges
  const tooltipWidth = 300; // Approximate max width
  const tooltipHeight = 250; // Approximate max height
  const edgeMargin = 10; // Minimum distance from screen edge
  
  // Calculate initial position
  let left = mousePosition.x + 10;
  let top = mousePosition.y - 10;
  
  // Check right edge
  if (left + tooltipWidth > window.innerWidth - edgeMargin) {
    // Position to the left of the cursor
    left = mousePosition.x - tooltipWidth - 10;
  }
  
  // Check left edge
  if (left < edgeMargin) {
    left = edgeMargin;
  }
  
  // Check bottom edge
  if (top + tooltipHeight > window.innerHeight - edgeMargin) {
    // Position above the cursor
    top = mousePosition.y - tooltipHeight - 10;
  }
  
  // Check top edge
  if (top < edgeMargin) {
    top = edgeMargin;
  }
  
  // On mobile, adjust positioning based on source
  const mobile = isMobile();
  if (mobile) {
    if (source === "hand") {
      // For hand tooltips: position at bottom of screen, centered horizontally
      left = Math.max(
        edgeMargin,
        Math.min(
          (window.innerWidth - tooltipWidth) / 2,
          window.innerWidth - tooltipWidth - edgeMargin
        )
      );
      top = window.innerHeight - tooltipHeight - edgeMargin - 20; // Extra margin from bottom
    } else if (tooltipWidth < window.innerWidth - 2 * edgeMargin) {
      // For building tooltips: center horizontally as before
      left = Math.max(
        edgeMargin,
        Math.min(
          mousePosition.x - tooltipWidth / 2,
          window.innerWidth - tooltipWidth - edgeMargin
        )
      );
    }
  }
  
  const tooltipStyle = {
    left: `${left}px`,
    top: `${top}px`,
  };

  return (
    <div
      className="fixed pointer-events-none z-50 bg-black/90 text-white rounded border border-gray-400 p-3 max-w-xs"
      style={tooltipStyle}
      data-testid="playable-tooltip"
    >
      {/* Header - Label and Icon */}
      <div className="flex items-center gap-2 mb-3">
        <Sprite
          filename="images/playables.png"
          height={32}
          width={32}
          {...cardSprite({ kind: playable.kind, level: playable.level })}
        />
        <span className="font-bold text-sm">{playable.label}</span>
        <span className="font-bold text-sm text-blue-400">{playable.kind}</span>
      </div>

      {/* Required Unlocks */}
      {playable.requiredUnlock.length > 0 && (
        <div className="mb-3 text-xs">
          <span className="text-yellow-300">Requires: </span>
          <span>{playable.requiredUnlock.join(", ")}</span>
        </div>
      )}

      {/* Two Column Section - Provides and Costs */}
      <div className="grid grid-cols-2 gap-4 mb-3">
        {/* Provides Column */}
        <div>
          <div className="text-green-400 font-semibold text-xs mb-1">
            Provides
          </div>
          <div className="space-y-1">
            {provides.map(([resource, value]) => (
              <div key={resource} className="flex items-center justify-between gap-1">
                <div className="flex items-center gap-1">
                  <Sprite
                    filename="/images/resources_20.png"
                    {...getResourceSprite(resource as GameResource)}
                    width={20}
                    height={20}
                    className="scale-75"
                  />
                  <span className="text-xs">{resource}</span>
                </div>
                <span className={`text-xs ${getValueColor(value)}`}>
                  {formatNumber(value, true)}
                </span>
              </div>
            ))}
            {provides.length === 0 && (
              <span className="text-gray-400 text-xs">None</span>
            )}
          </div>
        </div>

        {/* Costs Column */}
        <div>
          <div className="text-red-400 font-semibold text-xs mb-1">Costs</div>
          <div className="space-y-1">
            {costs.map(([resource, value]) => (
              <div key={resource} className="flex items-center justify-between gap-1">
                <div className="flex items-center gap-1">
                  <Sprite
                    filename="/images/resources_20.png"
                    {...getResourceSprite(resource as GameResource)}
                    width={20}
                    height={20}
                    className="scale-75"
                  />
                  <span className="text-xs">{resource}</span>
                </div>
                <span className={`text-xs ${getValueColor(value)}`}>
                  {formatNumber(value, true)}
                </span>
              </div>
            ))}
            {costs.length === 0 && (
              <span className="text-gray-400 text-xs">None</span>
            )}
          </div>
        </div>
      </div>

      {/* Combines Into */}
      {combinesInto && (
        <div className="border-t border-gray-600 pt-2">
          <div className="flex items-center gap-2">
            <span className="text-cyan-300 text-xs">Combines into:</span>
            <div className="w-4 h-4 overflow-hidden">
              <Sprite
                filename="images/playables.png"
                height={32}
                width={32}
                {...cardSprite({
                  kind: combinesInto.kind,
                  level: combinesInto.level,
                })}
                className="scale-50 origin-top-left"
              />
            </div>
            <span className="text-xs">{combinesInto.label}</span>
          </div>
        </div>
      )}
    </div>
  );
};
